import React from 'react';
import { Link } from 'react-router-dom';

import './css/Pricing.css'

import { Page } from './Page';
import { ProcessingPricing } from './ProcessingPricing';

import { trialPathFor } from './contact'

import { CUSTOM_TIER, PRICING_TIERS, PricingTier, TIER_INCLUDED, describeCustomTier, describeTier, formatAssets, formatPerAsset, formatPrice } from './pricingTiers'

// Every card is a link to the trial form, and names itself on the way there,
// so the request that follows says which card was clicked.
function TierCard( { tier, index }: { tier: PricingTier, index: number } ): React.JSX.Element {

	return (
		<Link
			className="Pricing-Tier"
			to={ trialPathFor( describeTier( tier ) ) }
			data-fade
			data-fade-delay={ ( 0.1 + index * 0.08 ).toFixed( 2 ) }
		>
			<div className="Pricing-Tier-Name" >{ tier.name }</div>
			<div className="Pricing-Tier-Assets" >{ formatAssets( tier.assets ) } assets</div>

			<div className="Pricing-Tier-Price" >
				<span className="Pricing-Tier-Amount" >{ formatPrice( tier.price ) }</span>
				<span className="Pricing-Tier-Period" >/ year</span>
			</div>
			<div className="Pricing-Tier-PerAsset" >{ formatPerAsset( tier ) } / asset / year</div>

			<ul className="Pricing-Tier-Included" >
				{ TIER_INCLUDED.map( ( item: string ): React.JSX.Element => (
					<li className="Pricing-Tier-Included-Item" key={ item } >{ item }</li>
				) ) }
			</ul>
		</Link>
	)
}

// Past the largest priced tier: the same card, with a quote standing where
// the others print their volume, so the four cards keep one line of big type.
function CustomCard( { index }: { index: number } ): React.JSX.Element {

	return (
		<Link
			className="Pricing-Tier"
			to={ trialPathFor( describeCustomTier() ) }
			data-fade
			data-fade-delay={ ( 0.1 + index * 0.08 ).toFixed( 2 ) }
		>
			<div className="Pricing-Tier-Name" >{ CUSTOM_TIER.name }</div>
			<div className="Pricing-Tier-Assets" >{ CUSTOM_TIER.price }</div>

			<div className="Pricing-Tier-PerAsset Pricing-Tier-Quote-Note" >{ CUSTOM_TIER.note }</div>
		</Link>
	)
}

// Standalone pricing page, in two parts under their own titles: the
// watermark, priced on the assets it protects, in tiers and a quote; then,
// set back under a rule, the pipeline on its own without the mark, priced
// on the cores it runs on.
export function Pricing(): React.JSX.Element {

	return (
		<Page title="Underlayer — Pricing" className="Pricing" >
			<div className="Pane-Light Page-Section Page-Section-First Pricing-Section" >
				<div className="Container" >

					<div className="Pricing-Head" >
						<div className="Title text-shadow-2" data-fade >Invisible Watermarking</div>
						<div className="Pricing-Intro Page-Text fs-18" data-fade data-fade-delay="0.1" >
							Simple pricing for large protected libraries: one commercial metric, the
							unique source assets you protect during the contract year.
						</div>
					</div>

					<div className="Pricing-Tiers" >
						{ PRICING_TIERS.map( ( tier: PricingTier, index: number ): React.JSX.Element => (
							<TierCard tier={ tier } index={ index } key={ tier.name } />
						) ) }
						<CustomCard index={ PRICING_TIERS.length } />
					</div>

					<div className="Pricing-Note fs-16" data-fade data-fade-delay="0.2" >
						Pricing is based on protected source assets, not on watermark operations.
						Re-encoding an asset for different users, channels, sessions or transactions
						does not consume additional asset capacity.
					</div>

					<div className="Pricing-Processing" >
						<div className="Pricing-Head" >
							<div className="Title text-shadow-2" data-fade >CPU Image Processing</div>
							<div className="Pricing-Intro Page-Text fs-18" data-fade data-fade-delay="0.1" >
								Blazing fast image proxy, without watermarking.
							</div>
						</div>

						<ProcessingPricing />
					</div>

				</div>
			</div>
		</Page>
	)
}
